import React, { useState } from 'react';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import { Link } from 'expo-router';
import { safeAccessibilityRole } from '@/constants/utils/a11y';
import { CtaRow, DisplayHeading, GhostCta, Kicker, LandingThemeProvider, PrimaryCta, useLanding } from '@/components/Landing/LandingKit';
import { Card, CheckLine, SectionIntro } from '@/components/Landing/MarketingKit';
import SemanticBlock from '@/components/Landing/SemanticBlock';
import UseCaseExamplePrompts from '@/components/Landing/UseCaseExamplePrompts';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import Text from '@/components/Typography/Text';
import { GAB_AI_SOFTWARE_ID } from '@/constants/config';
import { USE_CASE_CAPABILITIES, getUseCaseFeatureUses, safeUseCaseToolHref } from '../../../shared/public/use-case-capabilities.cjs';

import UseCaseShowcase from './UseCaseShowcase';
import UseCaseProductStory from './UseCaseProductStory';
import { isUseCaseProductDemo } from '../../../shared/public/use-case-demo.cjs';
import UseCaseToolkit from './UseCaseToolkit';
import Icon from '@/components/Icon';
import { getUseCaseVisuals } from '../../../shared/public/use-case-visuals.cjs';
import { isUseCaseSeoIndexable } from '../../../shared/public/use-cases.cjs';

const SITE = 'https://gab.ai';

export function buildVerticalStructuredData(data) {
  const pageUrl = `${SITE}/use-cases/${data.slug}`;
  const seo = data.seo || data.meta || {};
  const graph = [
    { '@type': 'WebPage', '@id': `${pageUrl}#webpage`, name: seo.title || data.name,
      description: seo.description || data.subtitle || '', url: pageUrl,
      isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: SITE }, inLanguage: 'en-US',
      ...(seo.ogImage ? { image: seo.ogImage } : {}) },
    { '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Use Cases', item: `${SITE}/use-cases` },
      { '@type': 'ListItem', position: 3, name: data.name, item: pageUrl },
    ] },
    { '@type': 'SoftwareApplication', '@id': GAB_AI_SOFTWARE_ID, name: 'Gab AI',
      applicationCategory: 'MultimediaApplication', operatingSystem: 'Web, iOS, Android, macOS, Windows',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' } },
  ];
  const featureUses = getUseCaseFeatureUses(data.featureUses);
  const tools = featureUses.length ? featureUses.map(item => ({ name: USE_CASE_CAPABILITIES[item.key].label, desc: item.description, href: USE_CASE_CAPABILITIES[item.key].href })) : data.tools;
  if (tools?.length) graph.push({ '@type': 'ItemList', name: `Gab AI tools for ${String(data.name || '').toLowerCase()}`,
    itemListElement: tools.map((tool, i) => ({ '@type': 'ListItem', position: i + 1, name: tool.name, description: tool.desc,
      ...(safeUseCaseToolHref(tool.href) ? { url: `${SITE}${tool.href}` } : {}) })) });
  if (data.faqs?.length) graph.push({ '@type': 'FAQPage', mainEntity: data.faqs.map(faq => ({ '@type': 'Question', name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a } })) });
  return { '@context': 'https://schema.org', '@graph': graph };
}

function PageSection({ label, children }) {
  const { pad, space } = useLanding();
  return <SemanticBlock tag="section" aria-label={label} style={{ width: '100%', alignItems: 'center', paddingLeft: pad, paddingRight: pad, paddingTop: space, paddingBottom: space }}>
    <View style={{ width: '100%', maxWidth: 1100 }}>{children}</View>
  </SemanticBlock>;
}

function GuideLink({ href, children }) {
  const { c } = useLanding();
  return <Link href={href} style={{ color: c.ink, fontSize: 15, lineHeight: 24, textDecorationLine: 'underline', paddingVertical: 10 }}>{children}</Link>;
}

function Question({ faq }) {
  const { c } = useLanding();
  const [open, setOpen] = useState(false);
  return <SemanticBlock tag="article" style={{ borderTopWidth: 1, borderColor: c.rule }}>
    <Pressable accessibilityRole="button" accessibilityLabel={faq.q} accessibilityState={{ expanded: open }} onPress={() => setOpen(v => !v)} style={{ minHeight: 64, paddingVertical: 18, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
      <Text tag="h3" weight="semibold" style={{ flex: 1, color: c.ink, fontSize: 17, lineHeight: 25 }}>{faq.q}</Text><Icon name={open ? 'minus' : 'plus'} size={14} color={c.muted} />
    </Pressable>
    {open && <Text tag="p" style={{ color: c.muted, fontSize: 15, lineHeight: 24, paddingBottom: 20 }}>{faq.a}</Text>}
  </SemanticBlock>;
}

export default function VerticalTemplate({ data, relatedUseCases = [] }) {
  return <LandingThemeProvider><VerticalPage data={data} relatedUseCases={relatedUseCases} /></LandingThemeProvider>;
}

function VerticalPage({ data, relatedUseCases }) {
  const { c, isXS, pad, space, width } = useLanding();
  const visualExamples = getUseCaseVisuals(data.visualExamples);
  const split = width >= 1000 && visualExamples.length > 0;
  const seo = data.seo || data.meta || {};
  const featureUses = getUseCaseFeatureUses(data.featureUses);
  const capabilities = (data.capabilities || []).filter(Boolean);
  const faqs = (data.faqs || []).filter(item => item.q && item.a);
  useLandingStyles();
  return <View style={{ flex: 1, backgroundColor: c.page }} {...(Platform.OS === 'web' ? { dataSet: { landingTheme: c.isDark ? 'dark' : 'light' } } : {})}>
    <MetaHead title={seo.title || data.name} description={seo.description || data.subtitle}
      canonical={`/use-cases/${data.slug}`} noindex={!isUseCaseSeoIndexable(data)} keywords={seo.keywords} image={seo.ogImage || undefined} structuredData={buildVerticalStructuredData(data)} />
    <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ alignItems: 'center' }}
      accessibilityRole={safeAccessibilityRole('main')} nativeID="main-content"
      {...(Platform.OS === 'web' ? { dataSet: { landingScroll: 'true' } } : {})}>
      {Platform.OS === 'web' && isUseCaseProductDemo(data.productDemo, data.examplePrompts) ? <UseCaseProductStory key={data.slug} data={data} relatedUseCases={relatedUseCases} /> : <>
      <SemanticBlock tag="header" style={{ width: '100%', alignItems: 'center', paddingLeft: pad, paddingRight: pad, paddingTop: isXS ? 84 : 112, paddingBottom: space }}>
        <View style={{ width: '100%', maxWidth: 1200, flexDirection: split ? 'row' : 'column', alignItems: split ? 'center' : 'stretch', gap: split ? 48 : 28 }}>
          <View style={{ flex: split ? 0.95 : undefined, minWidth: 0, gap: 18 }}>
            <Kicker>{data.eyebrow || `Gab AI for ${data.name}`}</Kicker>
            <DisplayHeading tag="h1" mask={false} type={{ fontSize: isXS ? 34 : split ? 46 : 54, lineHeight: isXS ? 41 : split ? 54 : 62, letterSpacing: isXS ? -1 : -1.6 }} lines={String(data.headline || data.name).split('\n')} style={{ maxWidth: 900 }} />
            {!!data.subtitle && <Text tag="p" style={{ color: c.muted, fontSize: isXS ? 16 : 18, lineHeight: isXS ? 25 : 29, maxWidth: 700 }}>{data.subtitle}</Text>}
            {(!isXS || !visualExamples.length) && <CtaRow><PrimaryCta label={data.cta || 'Try Gab AI free'} href="/signup" full={isXS} /><GhostCta label="Compare plans" href="/pricing" /></CtaRow>}
            {!!data.ctaSubtext && (!isXS || !visualExamples.length) && <Text tag="p" style={{ color: c.muted, fontSize: 13, lineHeight: 21 }}>{data.ctaSubtext}</Text>}
            {!isXS && <View style={{ flexDirection: 'row', gap: 18, flexWrap: 'wrap', marginTop: 8 }}>
              <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}><Icon name="message" size={15} color={c.muted} /><Text style={{ color: c.muted, fontSize: 13 }}>Explore in Chat</Text></View>
              <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}><Icon name="file-lines" size={15} color={c.muted} /><Text style={{ color: c.muted, fontSize: 13 }}>Create in Work</Text></View>
            </View>}
          </View>
          {!!visualExamples.length && <View style={{ flex: split ? 1.05 : undefined, minWidth: 0, width: '100%', maxWidth: split ? undefined : 720, alignSelf: 'center' }}><UseCaseShowcase key={data.slug} examples={visualExamples} prompts={data.examplePrompts} /></View>}
          {isXS && !!visualExamples.length && <CtaRow><PrimaryCta label={data.cta || 'Try Gab AI free'} href="/signup" full /><GhostCta label="Compare plans" href="/pricing" /></CtaRow>}
        </View>
      </SemanticBlock>
      <UseCaseExamplePrompts useCaseName={data.name} examplePrompts={data.examplePrompts} promptCategories={data.promptCategories} intro={visualExamples.length ? 'Find a useful starting point. Open a workflow for its sources, full prompt and review checks.' : data.description || data.subtitle} />
      {!!data.painPoints?.length && <PageSection label="Problems you can work through">
        <SectionIntro kicker="Where it helps" lines={['Work through the sticking points']} />
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 24, marginTop: 28 }}>{data.painPoints.filter(item => item.problem && item.solution).map(item => <View key={item.problem} style={{ gap: 10, width: isXS ? '100%' : '47%' }}><Icon name="arrow-right" size={20} color={c.muted} />
          <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 18, lineHeight: 26 }}>{item.problem}</Text>
          <Text tag="p" style={{ color: c.muted, fontSize: 15, lineHeight: 24 }}>{item.solution}</Text>
        </View>)}</View>
      </PageSection>}
      {featureUses.length > 0 ? <PageSection label="Relevant capabilities">
        <SectionIntro kicker="Your toolkit" lines={['Take the next step.']} />
        <UseCaseToolkit key={data.slug} items={featureUses} />
      </PageSection> : data.tools?.length ? <PageSection label="Relevant tools">
        <SectionIntro kicker="Your toolkit" lines={['Tools for your work']} />
        <View style={{ gap: 20, marginTop: 28 }}>{data.tools.map(tool => <View key={tool.name} style={{ gap: 8 }}>
          <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 18 }}>{tool.name}</Text>
          <Text tag="p" style={{ color: c.muted, fontSize: 15, lineHeight: 24 }}>{tool.desc}</Text>
          {!!safeUseCaseToolHref(tool.href) && <GuideLink href={tool.href}>Explore {tool.name}</GuideLink>}
        </View>)}</View>
      </PageSection> : null}
      {!!capabilities.length && <PageSection label="Other practical tasks">
        <SectionIntro kicker="More possibilities" lines={['Other work you can prepare']} />
        <View style={{ maxWidth: 800, marginTop: 28, gap: 16 }}>{capabilities.map(item => <CheckLine key={item} size={15}>{item}</CheckLine>)}</View>
      </PageSection>}
      {!!faqs.length && <PageSection label="Common questions">
        <SectionIntro kicker="FAQ" lines={['Before you start']} />
        <View style={{ maxWidth: 860, marginTop: 20 }}>{faqs.map(faq => <Question key={`${data.slug}-${faq.q}`} faq={faq} />)}</View>
      </PageSection>}
      <PageSection label="Start with one task">
        <Card style={{ gap: 16 }}>
          <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: 28, lineHeight: 36 }}>Start with one useful task.</Text>
          <Text tag="p" style={{ color: c.muted, fontSize: 15, lineHeight: 24 }}>Copy an example or open an editable draft. Add your references, remove confidential details, and review the instructions before you send. Chat and media use credits; Work requires Plus. Available models, costs and limits are shown in the app.</Text>
          <CtaRow><PrimaryCta label={data.cta || 'Try Gab AI free'} href="/signup" full={isXS} /><GhostCta label="View plans and credits" href="/pricing" /></CtaRow>
        </Card>
      </PageSection>
      {!!relatedUseCases.length && <PageSection label="Related use cases">
        <SectionIntro kicker="Explore more" lines={['Related ways to use Gab AI']} />
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 24, marginTop: 24 }}>{relatedUseCases.map(item => <GuideLink key={item.slug} href={`/use-cases/${item.slug}`}>{item.name}</GuideLink>)}</View>
        <GuideLink href="/use-cases">Browse all use cases</GuideLink>
      </PageSection>}
      </>}
      <PublicFooter variant="editorial" showHero={false} />
    </ScrollView>
  </View>;
}
